import React, { useContext, useEffect, useState } from "react";
import { Col, Form, Row } from "react-bootstrap";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import ESInput from "../../controls/ESInput";
import ESSearchDropdown from "../../controls/ESSearchDropdown";
import { UserContext } from "../../routes/routes";
import {
  getUserClassificationById,
  insertUserClassification,
  updateUserClassification,
} from "../../utils/ApiUrl";
import { axiosPost } from "../../utils/AxiosHelper";
import {
  checkIfSuperAdmin,
  getCachedData,
  Option,
  queryClient
} from "../../utils/common";
import { showToast, ToastTypeCode } from "../../utils/Constant";
import Loader from "../loader/Loader";
import "./UserClassificationList.scss";

interface UserClassification {
  ClassificationID?: number;
  ClassificationName: string;
  ClassificationDescription: string;
  IsActive: boolean;
  CreateUserId?: number;
  UpdateUserId?: number | null;
  CreateDate?: string;
  UpdateDate?: string | null;
}

interface AddUserClassificationProps {
  classificationId?: number | null;
  onClose: () => void;
}

const AddUserClassification: React.FC<AddUserClassificationProps> = ({
  classificationId,
  onClose,
}) => {
  const methods = useForm<UserClassification>({
    defaultValues: {
      ClassificationName: "",
      ClassificationDescription: "",
      IsActive: true,
    },
  });
  const [loading, setLoading] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [organizations, setOrganizations] = useState<Option[]>([]);
  const [selectedOrganization, setSelectedOrganization] = useState<number>(0);
  const { appContextdata } = useContext<any>(UserContext);

  const fetchClassificationDetailsById = async (classificationId: number) => {
    setLoading(true);
    axiosPost(getUserClassificationById, {
      ClassificationID: classificationId,
    })
      .then((response: any) => {
        if (response?.data?.Status === 200) {
          const data = response.data.ExtraData;
          const transformedData = {
            ...data,
            OrganizationId: organizations?.find(
              (org: Option) => org.value === data.OrganizationId
            )
              ? {
                  label: organizations?.find(
                    (org: Option) => org.value === data.OrganizationId
                  )?.label,
                  value: data.OrganizationId,
                }
              : null,
          };
          methods.reset(transformedData);
          setSelectedOrganization(data.OrganizationId);
        } else {
          showToast(`${response?.data?.ErrorMessage}`, ToastTypeCode?.ERROR);
        }
      })
      .catch((error) => {
        showToast("Error fetching classification details", ToastTypeCode?.ERROR);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (appContextdata?.Role) {
      const isAdmin = checkIfSuperAdmin(appContextdata);
      if (isAdmin) {
        if (classificationId && organizations?.length >= 1) {
          fetchClassificationDetailsById(classificationId);
        }
      } else {
        if (classificationId) {
          fetchClassificationDetailsById(classificationId);
        }
      }
    }
  }, [appContextdata?.Role, classificationId, organizations, methods]);

  const fetchData = async () => {
    if (appContextdata?.Role) {
      const isAdmin = checkIfSuperAdmin(appContextdata);
      if (isAdmin) {
        setIsSuperAdmin(true);
        const options = (await getCachedData('organizations')) as Option[];
        setOrganizations(options);
      }
    }
  };

  useEffect(() => {
    fetchData();
  }, [appContextdata?.Role]);

  const handleOrganizationChange = async (selectedOption: any) => {
    const organizationId = selectedOption?.value;
    setSelectedOrganization(organizationId);
  };

  const onSubmit: SubmitHandler<UserClassification> = async (data) => {
    const transformedData = {
      ...data,
      IsActive: data.IsActive ? 1 : 0,
      OrganizationId: selectedOrganization
        ? selectedOrganization
        : appContextdata?.Organization,
      CreateDate: new Date().toISOString()
    };
    setLoading(true);
    try {
      const response: any = classificationId
        ? await axiosPost(updateUserClassification, transformedData)
        : await axiosPost(insertUserClassification, transformedData);

      if (response?.data?.Status === 200) {
         queryClient.invalidateQueries("classification");
        const message = classificationId
          ? "Classification updated successfully"
          : "Classification created successfully";
        showToast(message, ToastTypeCode?.SUCCESS);
        onClose();
      } else {
        showToast(`${response?.data?.ErrorMessage}`, ToastTypeCode?.ERROR);
      }
    } catch (error) {
      showToast("Error saving classification", ToastTypeCode?.ERROR);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="sub-card">
        <div>
          {loading ? (
            <Loader />
          ) : (
            <FormProvider {...methods}>
              <form onSubmit={methods.handleSubmit(onSubmit)}>
                <Row>
                  {isSuperAdmin && (
                    <Form.Group as={Col} md="4">
                      <Form.Label>
                        Organization <span className="text-danger">*</span>
                      </Form.Label>
                      <ESSearchDropdown
                        name="OrganizationId"
                        placeholder="Organization"
                        options={organizations}
                        onChange={handleOrganizationChange}
                      />
                    </Form.Group>
                  )}
                  <Form.Group as={Col} md="3">
                    <Form.Label>
                      Classification Name
                      <span className="text-danger">*</span>
                    </Form.Label>

                    <ESInput
                      className="form-control"
                      name="ClassificationName"
                      placeholder="Classification name"
                      type="text"
                      required
                      minLength={2}
                      maxLength={200}
                    />
                  </Form.Group>
                  <Form.Group as={Col} md="3">
                    <Form.Label>Description</Form.Label>
                    <ESInput
                      className="form-control"
                      name="ClassificationDescription"
                      placeholder="Enter classification description"
                      type="text"
                      maxLength={500}
                    />
                  </Form.Group>
                  <Col md="2">
                    <div className="form-check">
                      <ESInput
                        name="IsActive"
                        type="checkbox"
                        placeholder={""}
                        className="form-check-input"
                      />
                      <label htmlFor="IsActive" className="form-check-label">Is Active</label>
                    </div>
                  </Col>
                  <Col md="4">
                    <button type="submit" className="save-btn">
                    {classificationId ? "Update" : "Save"}
                    </button>
                    <button
                      type="button"
                      className="back-btn"
                      onClick={onClose}
                    >
                      Cancel
                    </button>
                  </Col>
                </Row>
              </form>
            </FormProvider>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddUserClassification;
